import React from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Brain, 
  Database, 
  TrendingUp, 
  ShieldAlert, 
  Zap, 
  Layers,
  Search
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { 
  DemographicsChart, 
  CostAnalysisChart, 
  RegionalChart, 
  TrendChart 
} from '@/components/Charts';
import { PatientTable } from '@/components/DataTable';
import { FilterPanel } from '@/components/Filters';
import { 
  ThreeDScatterPlot, 
  ThreeDVolumeChart 
} from '@/components/3DVisualization';
import { useHealthcareData } from '@/hooks/useHealthcareData';
import { IMAGES } from '@/assets/images';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function Analytics() {
  const {
    isLoading,
    data: filteredData,
    kpis,
    regionalStats,
    updateFilter,
    filters,
  } = useHealthcareData();

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5, staggerChildren: 0.1 } },
  };

  const itemVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex h-[80vh] w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Activity className="h-12 w-12 animate-pulse text-primary" />
            <p className="text-lg font-medium text-muted-foreground">Processing Clinical Data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="relative min-h-screen space-y-8 p-6 lg:p-10">
        {/* Background Decorative Element */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <img 
            src={IMAGES.MEDICAL_BG_6} 
            alt="" 
            className="h-full w-full object-cover opacity-5"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
        </div>

        {/* Header Section */}
        <motion.div 
          variants={pageVariants} 
          initial="initial" 
          animate="animate"
          className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
        >
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Badge variant="outline" className="border-primary/20 bg-primary/5 text-primary">
                <Brain className="mr-1 h-3 w-3" /> AI-Powered Diagnostics 2026
              </Badge>
              <Badge variant="outline" className="border-accent/20 bg-accent/5 text-accent">
                <Zap className="mr-1 h-3 w-3" /> Real-time Updates
              </Badge>
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-foreground">
              Advanced Clinical <span className="text-primary">Analytics</span>
            </h1>
            <p className="mt-2 text-muted-foreground">
              Deep-dive into treatment effectiveness, cost-benefit correlations, and predictive patient risk modeling.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <FilterPanel 
              onFiltersChange={(newFilters) => {
                Object.entries(newFilters).forEach(([key, val]) => {
                  updateFilter(key as any, val);
                });
              }}
              activeFilters={filters}
            />
          </div>
        </motion.div>

        {/* Analytics Content Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:w-[400px]">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Layers className="h-4 w-4" /> Multi-Layer View
            </TabsTrigger>
            <TabsTrigger value="predictive" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Predictive Model
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-8">
            {/* 3D Exploration Section */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <Card className="relative overflow-hidden border-border/50 bg-card/50 backdrop-blur-md lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-primary" />
                    Spatial Correlation Analysis
                  </CardTitle>
                  <CardDescription>
                    Interactively explore the relationship between BMI, Age, and Insurance Charges in 3D space.
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-[450px] overflow-hidden rounded-b-xl">
                  <ThreeDScatterPlot data={filteredData} height={400} />
                </CardContent>
                <div className="absolute top-4 right-4">
                   <img src={IMAGES.MEDICAL_TECH_2} alt="Tech Overlay" className="h-12 w-12 rounded-full opacity-20 ring-1 ring-primary/30" />
                </div>
              </Card>

              <div className="space-y-6">
                <Card className="border-border/50 bg-card/50 backdrop-blur-md">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Treatment Effectiveness</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RegionalChart data={regionalStats} title="Regional Efficiency Score" />
                  </CardContent>
                </Card>
                
                <Card className="border-border/50 bg-card/50 backdrop-blur-md">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Cost Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CostAnalysisChart data={filteredData} title="Charges vs Age" />
                  </CardContent>
                </Card>
              </div>
            </motion.div>

            {/* Secondary Visuals */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="md:col-span-2">
                <Card className="border-border/50 bg-card/50 backdrop-blur-md">
                  <CardHeader>
                    <CardTitle>Demographic Density</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DemographicsChart data={filteredData} title="Age Grouping Analysis" />
                  </CardContent>
                </Card>
              </div>
              <div className="md:col-span-2">
                <Card className="border-border/50 bg-card/50 backdrop-blur-md">
                  <CardHeader>
                    <CardTitle>Temporal Cost Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <TrendChart data={filteredData} title="Projected vs Actual Charges" />
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="predictive" className="mt-6 space-y-8">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Card className="border-border/50 bg-card/50 backdrop-blur-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <ShieldAlert className="h-5 w-5 text-destructive" />
                        High-Risk Patient Profiling
                      </CardTitle>
                      <CardDescription>Identifying clusters with elevated risk scores</CardDescription>
                    </div>
                    <Badge variant="destructive">Alert: 12 High Risk</Badge>
                  </div>
                </CardHeader>
                <CardContent className="h-[400px]">
                  <ThreeDVolumeChart data={filteredData} height={350} />
                </CardContent>
              </Card>

              <Card className="border-border/50 bg-card/50 backdrop-blur-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-accent" />
                    Predictive Data Table
                  </CardTitle>
                  <CardDescription>Filtering clinical outcomes for model training</CardDescription>
                </CardHeader>
                <CardContent className="max-h-[400px] overflow-auto">
                  <PatientTable 
                    data={filteredData.slice(0, 10)} 
                    onRowClick={(p) => console.log('Drill down:', p.id)} 
                  />
                </CardContent>
              </Card>
            </div>

            <Card className="relative overflow-hidden border-border/50 bg-primary/5">
              <div className="flex flex-col items-center gap-6 p-8 md:flex-row">
                <img 
                  src={IMAGES.HEALTHCARE_DATA_6} 
                  alt="AI Insight" 
                  className="h-32 w-32 rounded-2xl object-cover shadow-2xl ring-2 ring-primary/20"
                />
                <div className="flex-1 space-y-2">
                  <h3 className="text-xl font-bold text-foreground">2026 Predictive Health Insight</h3>
                  <p className="text-muted-foreground">
                    Our neural network models suggest a <span className="font-bold text-primary">14.2% potential reduction</span> in overall treatment costs if smoking cessation programs are prioritized in the Southeast region. 
                    Data shows a strong correlation between BMI over 30 and emergency re-admissions in patients aged 45-60.
                  </p>
                  <div className="pt-2">
                    <Badge className="bg-primary text-primary-foreground">Strategy Recommended</Badge>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Data Detail Section */}
        <motion.div variants={itemVariants}>
          <Card className="border-border/50 bg-card/50 backdrop-blur-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Detailed Patient Population</CardTitle>
                <CardDescription>Total of {filteredData.length} records matching current criteria</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <PatientTable 
                data={filteredData} 
                onRowClick={(patient) => {
                  // Future implementation for patient drill-down
                  console.log("Patient Details:", patient);
                }} 
              />
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </Layout>
  );
}
